import { ErrorEvent, EventHint } from '@sentry/react';

import { HttpClientError } from '../types/HttpClientError';
import { buildHttpFingerprint } from './buildHttpFingerprint';
import { shouldDropEvent } from './shouldDropEvent';

/**
 * Sentry `beforeSend` adapter. Vendor-specific glue that:
 *
 *   1. Extracts the fields the pre-send pipeline cares about from the
 *      Sentry-shaped event/hint pair.
 *   2. Delegates the filter and fingerprint decisions to the pure
 *      helpers in `shouldDropEvent.ts` and `buildHttpFingerprint.ts`.
 *   3. Returns the (possibly mutated) Sentry event, or `null` to drop it.
 *
 * When swapping vendors, write the equivalent thin orchestrator against
 * the new vendor's pre-send hook - the helpers are reused unchanged.
 */
export const beforeSend = (
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null => {
  const error = (hint.originalException ?? {}) as HttpClientError;
  const errorCode = error.code ?? '';
  const errorRequestUrl = error.config?.url ?? '';
  const errorRequestMethod = error.config?.method ?? '';
  const errorResponseStatus = error.response?.status ?? '';

  // We extract the error message from the event because the
  // originalException from hint could be of different types (Array,
  // HttpClientError, ...). The event exception value is consistently reliable.
  const errorMessage = event.exception?.values?.[0]?.value ?? '';

  if (shouldDropEvent({ errorCode, errorMessage, errorRequestUrl })) {
    return null;
  }

  if (errorRequestUrl) {
    event.fingerprint = buildHttpFingerprint({
      errorCode,
      errorRequestMethod,
      errorRequestUrl,
      errorResponseStatus,
    });
  }

  return event;
};
